import {
  animate,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  HostBinding,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  ParamMap,
  ResolveEnd,
  ResolveStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import {
  DropdownComponent,
  InfoBannerComponent,
  ToastComponent,
} from '@catch/general-ui';
import {
  ConnectionService,
  languages,
  LanguageService,
  ScreenService,
  ThemingService,
  ToastsService,
} from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgSelectConfig } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  TourAnchorMatMenuDirective,
  TourMatMenu,
  TourService as UiTourService,
} from 'ngx-ui-tour-md-menu';
import posthog from 'posthog-js';
import { merge, Observable } from 'rxjs';
import {
  delay,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  authenticationAnimation,
  fadeInOut,
  fadeInOutUp,
  loginAnimation,
  loginBackgroundAnimation,
  toastInOut,
  viewLoader,
} from '../assets/animations/animations';
import { environment } from '../environments/environment';
import { AuthenticationAdapter } from './adapters/authentication.adapter';
import { CreditsBookingModalComponent } from './components/credits-booking-modal/credits-booking-modal.component';
import { CreditsLogModalComponent } from './components/credits-log-modal/credits-log-modal.component';
import { AppState } from './app.reducer';
import { NPSModalComponent } from './components/nps-modal/nps-modal.component';
import { ReleaseNotesModalComponent } from './components/release-notes-modal/release-notes-modal.component';
import { OnboardingModalComponent } from './components/onboarding-modal/onboarding-modal.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { AddTfaModalComponent } from './components/tfa/add-tfa-modal/add-tfa-modal.component';
import { TfaBlockModalComponent } from './components/tfa/tfa-block-modal/tfa-block-modal.component';
import { TopnavComponent } from './components/topnav/topnav.component';
import { TosModalComponent } from './components/tos-modal/tos-modal.component';
import { ViewLoaderComponent } from './components/view-loader/view-loader.component';
import { CompanyUserRole } from './enums/company-user.role.enum';
import { UserAccount } from './interfaces/user-account';
import { AuthHeroCarouselComponent } from './components/auth-hero-carousel/auth-hero-carousel.component';
import { MeshGradientComponent } from './components/mesh-gradient/mesh-gradient.component';
import { ButtonComponent } from './components/button/button.component';
import { InvoiceBillingModalComponent } from './modules/settings-company/components/invoice-billing-modal/invoice-billing-modal.component';
import { FontawesomeService } from './services/fontawesome.service';
import { CreditsBookingModalHostService } from './services/credits-booking-modal-host.service';
import { CreditsLogModalHostService } from './services/credits-log-modal-host.service';
import { InvoiceBillingModalHostService } from './services/invoice-billing-modal-host.service';
import { RouterService } from './services/router.service';
import { ShortcutsService } from './services/shortcuts.service';
import { TourService } from './services/tour.service';
import { loginSuccess } from './store/auth/auth.actions';
import {
  hasPermission,
  selectLoggedIn,
  selectUserLoggingIn,
} from './store/auth/auth.reducer';
import { loadCompanyAccountSuccess } from './store/company-account/company-account.actions';
import { closeSidenav, openSidenav } from './store/config/config.actions';
import { loadUserAccountSuccess } from './store/user-account/user-account.actions';
import {
  selectUserAccount,
  selectUserAccountLoading,
} from './store/user-account/user-account.reducer';
import { loadUserAvatar } from './store/user-avatar/user-avatar.actions';
import { selectUserAvatarUrl } from './store/user-avatar/user-avatar.reducer';
import { transitionString } from './constants/transition';
import { CompanionService } from './services/companion.service';
import { identify as identifyDash0 } from '@dash0/sdk-web';
import { DateTime } from 'luxon';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    AsyncPipe,
    RouterOutlet,
    TranslocoPipe,
    FontAwesomeModule,
    ButtonComponent,
    TourMatMenu,
    TopnavComponent,
    SidenavComponent,
    ViewLoaderComponent,
    OnboardingModalComponent,
    NPSModalComponent,
    TfaBlockModalComponent,
    AddTfaModalComponent,
    TosModalComponent,
    DropdownComponent,
    InfoBannerComponent,
    ToastComponent,
    MeshGradientComponent,
    AuthHeroCarouselComponent,
    CreditsBookingModalComponent,
    CreditsLogModalComponent,
    InvoiceBillingModalComponent,
    ReleaseNotesModalComponent,
  ],
  animations: [
    trigger('routeAnimations', [
      transition('* => *', [
        query(
          ':leave',
          style({
            opacity: 1,
            position: 'absolute',
            width: '100%',
          }),
          { optional: true },
        ),
        query(
          ':enter',
          style({
            opacity: 0,
            position: 'absolute',
            width: '100%',
          }),
          { optional: true },
        ),
        query(':leave', animate(transitionString, style({ opacity: 0 })), {
          optional: true,
        }),
        query(':enter', animate(transitionString, style({ opacity: 1 })), {
          optional: true,
        }),
      ]),
    ]),
    fadeInOutUp,
    fadeInOut,
    viewLoader,
    loginAnimation,
    toastInOut,
    loginBackgroundAnimation,
    authenticationAnimation,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly creditsBookingModalHost = inject(
    CreditsBookingModalHostService,
  );
  private readonly creditsBookingModal = viewChild(
    CreditsBookingModalComponent,
  );
  private readonly creditsLogModalHost = inject(CreditsLogModalHostService);
  private readonly creditsLogModal = viewChild(CreditsLogModalComponent);
  private readonly invoiceBillingModalHost = inject(
    InvoiceBillingModalHostService,
  );
  private readonly invoiceBillingModal = viewChild(
    InvoiceBillingModalComponent,
  );
  public readonly companionService = inject(CompanionService);

  private readonly actions$ = inject(Actions);
  private readonly authenticationAdapter = inject(AuthenticationAdapter);
  private readonly ngSelectConfig = inject(NgSelectConfig);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly shortcutsService = inject(ShortcutsService);
  private readonly store = inject(Store<AppState>);
  private readonly swUpdate = inject(SwUpdate);
  private readonly translateService = inject(TranslocoService);
  public readonly connectionService = inject(ConnectionService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly languageService = inject(LanguageService);
  public readonly routerService = inject(RouterService);
  public readonly screenService = inject(ScreenService);
  public readonly themingService = inject(ThemingService);
  public readonly toastsService = inject(ToastsService);
  public readonly tourService = inject(TourService);
  public readonly uiTourService = inject(UiTourService);

  @HostBinding('class.logging-in') loginHideOverflow: boolean = false;
  languages = languages.filter((lang) => lang.value !== 'fr-FR');
  version = `Version ${environment.version.replace(/-/g, '.')}`;
  year = DateTime.now().year;

  loading$: Observable<boolean>;
  private showLoading$: Observable<boolean>;
  private hideLoading$: Observable<boolean>;

  sidenavOpen$: Observable<boolean>;
  loggedIn$: Observable<boolean>;
  userLoggingIn$: Observable<any>;
  isAdmin$: Observable<boolean>;

  showLoginAnimation: boolean;
  avatarUrl$!: Observable<string>;
  userAccountLoading$!: Observable<boolean>;
  userAccount$!: Observable<UserAccount>;

  backgroundRandomizer = signal(Math.floor(Math.random() * 6) + 1);

  maintenanceInfo = signal(null);

  constructor() {
    effect(() => {
      const modal = this.creditsBookingModal();
      if (modal) {
        this.creditsBookingModalHost.register(modal);
      }
    });
    effect(() => {
      const modal = this.creditsLogModal();
      if (modal) {
        this.creditsLogModalHost.register(modal);
      }
    });
    effect(() => {
      const modal = this.invoiceBillingModal();
      if (modal) {
        this.invoiceBillingModalHost.register(modal);
      }
    });
  }

  ngOnInit(): void {
    // Set up periodic update checks (every 30 seconds)
    if (this.swUpdate.isEnabled) {
      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 30000);
    }

    // Add global test function (development only)
    if (!environment.production) {
      (window as any).testUpdateToast = () => this.testUpdateToast();
    }

    this.showLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveStart),
      map(() => true),
    );
    this.hideLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveEnd),
      map(() => false),
    );
    this.loading$ = merge(this.showLoading$, this.hideLoading$);

    this.sidenavOpen$ = this.screenService.isSmallScreen$.pipe(
      startWith(this.screenService.isSmallScreen),
      map((isSmallScreen) => !isSmallScreen),
    );
    this.store.dispatch(
      this.screenService.isSmallScreen ? closeSidenav() : openSidenav(),
    );
    this.screenService.isSmallScreen$
      .pipe(untilDestroyed(this))
      .subscribe((isSmallScreen) => {
        this.store.dispatch(isSmallScreen ? closeSidenav() : openSidenav());
      });
    this.loggedIn$ = this.store.select(selectLoggedIn);
    this.userLoggingIn$ = this.store.select(selectUserLoggingIn);
    this.avatarUrl$ = this.store.select(selectUserAvatarUrl);
    this.userAccountLoading$ = this.store.select(selectUserAccountLoading);
    this.userAccount$ = this.store.select(selectUserAccount);
    this.isAdmin$ = this.store.select(hasPermission(CompanyUserRole.ADMIN));

    this.actions$
      .pipe(
        ofType(loginSuccess),
        tap(() => this.store.dispatch(loadUserAvatar())),
        tap(() => {
          this.showLoginAnimation = true;
          this.loginHideOverflow = true;
        }),
        switchMap(() => this.actions$.pipe(ofType(loadUserAccountSuccess))),
        delay(500),
        tap(() => (this.showLoginAnimation = false)),
        delay(2000),
        tap(() => (this.loginHideOverflow = false)),
        untilDestroyed(this),
      )
      .subscribe();

    this.actions$
      .pipe(ofType(loadUserAccountSuccess), take(1))
      .subscribe((user) => {
        posthog.identify(user.account.id.toString(), {
          email: user.account.email,
          firstName: user.account.firstName,
          lastName: user.account.lastName,
        });
        identifyDash0(user.account.id.toString(), {
          name: user.account.firstName,
          fullName: user.account.firstName + ' ' + user.account.lastName,
          email: user.account.email,
          roles: [user.account.companyRole?.toString() ?? 'user'],
        });
        this.tourService.init();
      });
    this.actions$
      .pipe(ofType(loadCompanyAccountSuccess), take(1))
      .subscribe((company) => {
        posthog.group('company', company.account.companyID.toString(), {
          name: company.account.companyName,
        });
      });

    if (!environment.debug) {
      this.translateService
        .selectTranslate([
          'ct-library.user-console-stop',
          'ct-library.user-console-warning',
        ])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result) => {
            console.info(
              `%c${result['ct-library.user-console-stop']}`,
              'color: red; font-size: 30px; font-weight: bold;',
            );
            console.info(
              `%c${result['ct-library.user-console-warning']}`,
              'font-size: 20px; font-weight: bold;',
            );
          },
        });
    }

    this.translateService
      .selectTranslate('ct-library.constants.ng-select.placeholders.not-found')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.ngSelectConfig.notFoundText = result;
        },
      });

    this.translateService
      .selectTranslate(
        'ct-library.constants.ng-select.placeholders.placeholder',
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.ngSelectConfig.placeholder = result;
        },
      });

    this.swUpdate.versionUpdates
      .pipe(untilDestroyed(this))
      .subscribe((update) => {
        if (update.type === 'VERSION_READY') this.toastsService.update();
      });
  }

  ngAfterViewInit(): void {
    this.checkForEmailVerificationNonce();
    //this.tourService.startDashboardTour();
  }

  async checkForEmailVerificationNonce(): Promise<void> {
    this.route.queryParamMap
      .pipe(map((params: ParamMap) => params.get('email-verification-nonce')))
      .subscribe((nonce) => {
        if (nonce) {
          this.authenticationAdapter
            .emailVerification({ nonce })
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this.toastsService.success(
                  'company-tool.views.authentication.login.success-toast-verify',
                );
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: { 'email-verification-nonce': null },
                  queryParamsHandling: 'merge',
                });
              },
              error: () => {
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: { 'email-verification-nonce': null },
                  queryParamsHandling: 'merge',
                });
              },
            });
        }
      });
  }

  reloadPage(): void {
    location.reload();
  }

  // Test function for UPDATE toast (development only)
  testUpdateToast(): void {
    if (!environment.production) {
      this.toastsService.update();
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet.isActivated ? outlet.activatedRoute : '';
  }

  getAvatar(id: number): string {
    return `${environment.apiBaseUrl}/company/user/${id}/avatar`;
  }

  setAuthThemeMode(mode: 'light' | 'dark' | 'auto'): void {
    this.themingService.setMode(mode);
  }
}
